import { localDayKey, formatDayLabel, toEpochMs } from "./thread-time"

/** One render item: either a day separator or a message (with a grouped flag). */
export type ThreadItem<M> =
  | { readonly kind: "separator"; readonly id: string; readonly label: string }
  | { readonly kind: "message"; readonly message: M; readonly grouped: boolean }

/** Consecutive same-author messages closer than this collapse their label. */
const GROUP_WINDOW_MS = 5 * 60 * 1000

interface BuildOptions {
  readonly showDaySeparators: boolean
  readonly groupConsecutive: boolean
  /** "Now" in epoch millis — defaults to Date.now(); injected in tests for determinism.
   *  Kept in this lib (not the component) so the impure read stays out of render. */
  readonly now?: number
}

/**
 * Flattens a chronological message list into render items:
 *  - inserts a day separator when the local-day bucket changes (if enabled), and
 *  - marks a message `grouped` when it follows a same-author message within 5
 *    minutes (if enabled) so the bubble can drop the repeated author label.
 *
 * Pure and timezone-aware (buckets by the viewer's local day). A separator always
 * resets grouping, so the first message of each day keeps its label. Messages are
 * assumed sorted ascending by `createdAt`.
 */
export function buildThreadItems<
  M extends { id: string; authorType: string; createdAt: unknown },
>(messages: readonly M[], options: BuildOptions): ReadonlyArray<ThreadItem<M>> {
  const now = options.now ?? Date.now()
  const items: Array<ThreadItem<M>> = []
  let prevDayKey: string | null = null
  let prevAuthor: string | null = null
  let prevMs = 0

  for (const message of messages) {
    const ms = toEpochMs(message.createdAt)
    const dayKey = localDayKey(ms)
    let startedDay = false

    if (options.showDaySeparators && dayKey !== prevDayKey) {
      items.push({
        kind: "separator",
        id: `sep-${dayKey}-${message.id}`,
        label: formatDayLabel(ms, now),
      })
      startedDay = true
      prevDayKey = dayKey
    }

    const grouped =
      options.groupConsecutive &&
      !startedDay &&
      prevAuthor === message.authorType &&
      ms - prevMs < GROUP_WINDOW_MS

    items.push({ kind: "message", message, grouped })
    prevAuthor = message.authorType
    prevMs = ms
  }

  return items
}
